import { useState, type FormEvent } from 'react';
import { ContactConsentBox } from './ContactConsent.js';
import {
  EMPTY_DEAL_FORM,
  SAMPLE_DEAL_FORM,
  deriveDeal,
  missingCoreFields,
  type DealFormState,
} from '../deal-form.js';

interface NumField {
  key: keyof DealFormState;
  label: string;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  required?: boolean;
}

// Placeholders are prefixed "e.g." so an EMPTY field never masquerades as an entered value.
const CAPITAL: NumField[] = [
  {
    key: 'totalCost',
    label: 'Total dev cost (TDC)',
    prefix: '$',
    placeholder: 'e.g. 37,180,000',
    required: true,
  },
  {
    key: 'loanAmount',
    label: 'Senior loan',
    prefix: '$',
    placeholder: 'e.g. 26,026,000',
    required: true,
  },
  { key: 'totalUnits', label: 'Units', placeholder: 'e.g. 166', required: true },
  { key: 'holdYears', label: 'Hold (years)', placeholder: 'e.g. 10' },
];
const INCOME: NumField[] = [
  {
    key: 'noi',
    label: 'Stabilized NOI',
    prefix: '$',
    placeholder: 'e.g. 2,220,000',
    required: true,
  },
  {
    key: 'annualDebtService',
    label: 'Annual debt service',
    prefix: '$',
    placeholder: 'e.g. 1,694,656',
  },
  { key: 'salePrice', label: 'Exit sale price', prefix: '$', placeholder: 'e.g. 41,800,000' },
];
const RATES: NumField[] = [
  {
    key: 'exitCapRatePct',
    label: 'Exit cap rate',
    suffix: '%',
    placeholder: 'e.g. 5.5',
    required: true,
  },
  { key: 'vacancyRatePct', label: 'Vacancy', suffix: '%', placeholder: 'e.g. 7' },
  { key: 'expenseRatioPct', label: 'Expense ratio', suffix: '%', placeholder: 'e.g. 35' },
  { key: 'concessionRatePct', label: 'Market concession', suffix: '%', placeholder: 'e.g. 26.7' },
];

interface DealFormProps {
  submitting: boolean;
  error: string | null;
  /**
   * The derived deal PLUS the client's answer to the consent box. The answer rides beside the
   * deal rather than inside it: `deriveDeal` produces the underwriting inputs, and consent is a
   * fact about the submission, not about the economics — the same separation `site` and
   * `sponsorName` already have.
   */
  onSubmit: (payload: ReturnType<typeof deriveDeal> & { contactConsent: boolean }) => void;
}

export function DealForm({ submitting, error, onSubmit }: DealFormProps) {
  const [form, setForm] = useState<DealFormState>(EMPTY_DEAL_FORM);
  // Ground Truth §2.4: off by default, never pre-selected. Local per-submission state,
  // deliberately NOT seeded from a profile, a previous deal, or storage.
  const [contactConsent, setContactConsent] = useState(false);
  const [missing, setMissing] = useState<Set<keyof DealFormState>>(new Set());

  function set<K extends keyof DealFormState>(key: K, value: DealFormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    // Clear a field's error as soon as the user starts typing into it.
    if (missing.has(key)) {
      setMissing((m) => {
        const next = new Set(m);
        next.delete(key);
        return next;
      });
    }
  }

  function loadSample() {
    setForm(SAMPLE_DEAL_FORM);
    setMissing(new Set());
  }

  function clearForm() {
    setForm(EMPTY_DEAL_FORM);
    setMissing(new Set());
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const gaps = missingCoreFields(form);
    if (gaps.length > 0) {
      setMissing(new Set(gaps));
      return;
    }
    setMissing(new Set());
    onSubmit({ ...deriveDeal(form), contactConsent });
  }

  function renderGroup(title: string, fields: NumField[]) {
    return (
      <fieldset className="df__group">
        <legend>{title}</legend>
        <div className="df__grid">
          {fields.map((f) => (
            <label
              className={`df__field${missing.has(f.key) ? ' df__field--error' : ''}`}
              key={f.key}
            >
              <span className="df__label">
                {f.label}
                {f.required && (
                  <span className="df__req" aria-hidden="true">
                    {' '}
                    *
                  </span>
                )}
              </span>
              <span className="df__inputwrap">
                {f.prefix && <span className="df__affix">{f.prefix}</span>}
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={f.placeholder}
                  value={form[f.key] as string}
                  onChange={(e) => set(f.key, e.target.value)}
                />
                {f.suffix && <span className="df__affix df__affix--suffix">{f.suffix}</span>}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  return (
    <form className="df" onSubmit={handleSubmit}>
      <div className="df__bar">
        <button type="button" className="link-btn" onClick={loadSample} disabled={submitting}>
          Load sample (The Commons)
        </button>
        {!isBlank(form) && (
          <button type="button" className="link-btn" onClick={clearForm} disabled={submitting}>
            Clear
          </button>
        )}
      </div>

      {renderGroup('Capital structure', CAPITAL)}
      {renderGroup('Income & exit', INCOME)}
      {renderGroup('Rates', RATES)}

      <fieldset className="df__group">
        <legend>Classification</legend>
        <div className="df__grid">
          <label className="df__field">
            <span className="df__label">Asset class</span>
            <span className="df__inputwrap">
              <input
                type="text"
                placeholder="e.g. Multifamily"
                value={form.assetClass}
                onChange={(e) => set('assetClass', e.target.value)}
              />
            </span>
          </label>
          {/*
            Sponsor sits in Classification, not in a group of its own: it identifies the deal rather
            than pricing it, and a lone fieldset would give a one-line optional field more visual
            weight than the required numbers above it (canon R9 — visual calm).
          */}
          <label className="df__field">
            <span className="df__label">Sponsor / borrower</span>
            <span className="df__inputwrap">
              <input
                type="text"
                placeholder="e.g. Ridgeline Development LLC"
                value={form.sponsorName}
                onChange={(e) => set('sponsorName', e.target.value)}
              />
            </span>
            <span className="df__hint">
              Used to look up public SEC filings. Leave blank to skip — nothing else changes.
            </span>
          </label>
          <label className="df__field">
            <span className="df__label">Property class</span>
            <span className="df__inputwrap">
              <input
                type="text"
                placeholder="e.g. B"
                value={form.propertyClass}
                onChange={(e) => set('propertyClass', e.target.value)}
              />
            </span>
          </label>
          <label className="df__check">
            <input
              type="checkbox"
              checked={form.isLIHTC}
              onChange={(e) => set('isLIHTC', e.target.checked)}
            />
            <span>LIHTC (affordable) — suspends the market-rate dev-spread gate</span>
          </label>
        </div>
      </fieldset>

      {/*
        Sponsor evidence is its own full-width field and sits ABOVE reviewer context deliberately.
        It is the only optional input on this form that moves a scored category — 20 of the 100
        points — so burying it under a general notes box would understate it. The label says what it
        feeds, because a field whose effect is invisible does not get filled in (canon R8/R10).
      */}
      <label className="df__field df__field--full">
        <span className="df__label">Sponsor &amp; development team (optional)</span>
        <textarea
          rows={3}
          placeholder="What have they built? e.g. Ridgeline has delivered 9 multifamily projects since 2011 (2,400 units, Raleigh–Durham). Principals: J. Marek (28 yrs), A. Solano (16 yrs). GC: Hoffman Construction — 6 prior projects together, all delivered on schedule."
          value={form.sponsorContext}
          onChange={(e) => set('sponsorContext', e.target.value)}
        />
        <span className="df__hint">
          Scored — this is the evidence behind Sponsor &amp; Execution, 20 of the 100 points. Left
          blank, that category is judged with no sponsor information and reported as unevidenced.
        </span>
      </label>

      <label className="df__field df__field--full">
        <span className="df__label">Reviewer context (optional)</span>
        <textarea
          rows={2}
          // ⚠️ Deliberately no longer says "sponsor track record" or "GC relationship". This field is a
          // class/amenity override read by dealMechanic / getCanonicalClass — it never reaches the
          // Sponsor module. The old placeholder invited a track record into a field that cannot score
          // one, so anyone who followed it got no sponsor credit and no indication why.
          placeholder="Market notes, asset positioning, anything the analyst should weigh…"
          value={form.reviewerContext}
          onChange={(e) => set('reviewerContext', e.target.value)}
        />
      </label>

      {missing.size > 0 && (
        <div className="notice error">
          Enter the required fields (marked <span className="df__req">*</span>) before running: NOI,
          total dev cost, senior loan, exit cap rate, and units.
        </div>
      )}
      {error && <div className="notice error">{error}</div>}

      <ContactConsentBox
        checked={contactConsent}
        onChange={setContactConsent}
        disabled={submitting}
      />

      <button type="submit" disabled={submitting}>
        {submitting ? 'Starting analysis…' : 'Run premium analysis'}
      </button>
    </form>
  );
}

function isBlank(form: DealFormState): boolean {
  return (Object.keys(form) as (keyof DealFormState)[]).every((k) => {
    const v = form[k];
    return typeof v === 'boolean' ? v === false : v === '';
  });
}
